import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { Loader2, Plus, Trash2, Edit3, History, Building2, DoorOpen, Users, Boxes, ShieldCheck, CalendarClock, Globe, Download, StickyNote, Upload, Map as MapIcon, Search, X, ChevronDown, ChevronRight, LayoutGrid, Table2 } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { AvailableFromView } from './AvailableFromView';
import { CrossCentreView } from './CrossCentreView';
import { AlertsBanner } from './AlertsBanner';
import { HistoryTimelineView } from './HistoryTimelineView';

const ALL_CENTRES = '__all__';

const COMPONENT_TYPES = [
  { value: 'workstations', label: 'Workstations' },
  { value: 'cabin', label: 'Cabin' },
  { value: 'meeting_room', label: 'Meeting Room' },
];

const emptyComponent = () => ({ type: 'workstations', seats: 1 });

const componentLabel = (c) => {
  if (c.type === 'workstations') return `${c.seats} Workstations`;
  if (c.type === 'cabin') return `${c.seats}-seater Cabin`;
  if (c.type === 'meeting_room') return `${c.seats}-seater Meeting Room`;
  return `${c.seats} seats`;
};

// ---------------- Create / Edit modal ---------------- //
const UnitFormModal = ({ open, onClose, unit, venueId, venues = [], api, onSaved }) => {
  const [name, setName] = useState('');
  const [floor, setFloor] = useState('');
  const [components, setComponents] = useState([emptyComponent()]);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const venueFloors = useMemo(() => venues.find(v => v.id === venueId)?.floors || [], [venues, venueId]);

  useEffect(() => {
    if (open) {
      setName(unit?.name || '');
      setFloor(unit?.floor || '');
      setComponents(unit?.components?.length ? unit.components.map(c => ({ ...c })) : [emptyComponent()]);
      setNotes(unit?.notes || '');
    }
  }, [open, unit]);

  const totalSeats = useMemo(
    () => components.reduce((sum, c) => sum + (parseInt(c.seats) || 0), 0),
    [components]
  );
  const category = totalSeats >= 20 ? 'MO' : 'CW';

  const updateComponent = (idx, patch) => {
    setComponents(prev => prev.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
  };

  const removeComponent = (idx) => setComponents(prev => prev.filter((_, i) => i !== idx));

  const save = async () => {
    if (!name.trim()) return toast.error('Unit name is required');
    if (venueFloors.length && !floor) return toast.error('Select a floor for this unit');
    if (!components.length) return toast.error('Add at least one component');
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        floor: floor || null,
        components: components.map(c => ({ type: c.type, seats: Math.max(0, parseInt(c.seats) || 0) })),
        notes: notes.trim() || null,
      };
      if (unit) {
        await api.patch(`/inventory/units/${unit.id}`, payload);
        toast.success('Unit updated');
      } else {
        await api.post('/inventory/units', { ...payload, venue_id: venueId });
        toast.success('Unit created');
      }
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="inventory-unit-form">
        <DialogHeader>
          <DialogTitle>{unit ? `Edit Unit — ${unit.name}` : 'Create Inventory Unit'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label>Unit name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="MO1, Ground Floor Cabin A, …" data-testid="unit-name-input" />
          </div>
          <div>
            <Label>Floor {venueFloors.length ? '*' : '(optional — no floors configured for this venue)'}</Label>
            {venueFloors.length ? (
              <Select value={floor} onValueChange={setFloor}>
                <SelectTrigger data-testid="unit-floor-select"><SelectValue placeholder="Select floor" /></SelectTrigger>
                <SelectContent>
                  {venueFloors.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-xs text-muted-foreground italic py-1">
                Ask an admin to configure floors for this venue under Venues → Floors Config.
              </p>
            )}
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Components</Label>
              <Button type="button" size="sm" variant="outline" onClick={() => setComponents(prev => [...prev, emptyComponent()])} data-testid="add-component-btn">
                <Plus className="w-3 h-3 mr-1" /> Add
              </Button>
            </div>
            <div className="space-y-2">
              {components.map((c, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`component-row-${i}`}>
                  <Select value={c.type} onValueChange={(v) => updateComponent(i, { type: v })}>
                    <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {COMPONENT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input
                    type="number" min={0} value={c.seats}
                    onChange={(e) => updateComponent(i, { seats: Math.max(0, parseInt(e.target.value) || 0) })}
                    className="w-24" placeholder="Seats"
                  />
                  <span className="text-xs text-muted-foreground flex-1">{c.type === 'workstations' ? 'seats' : 'seat capacity'}</span>
                  {components.length > 1 && (
                    <Button type="button" size="icon" variant="ghost" onClick={() => removeComponent(i)}>
                      <Trash2 className="w-3 h-3 text-red-500" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="p-3 rounded-md bg-muted/40 text-xs flex items-center justify-between">
            <span>Total seats</span>
            <span className="font-semibold">
              {totalSeats}
              <Badge variant="outline" className="ml-2">{category}</Badge>
            </span>
          </div>
          <div>
            <Label>Notes (optional)</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional context (layout, quirks, etc.)" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="unit-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            {unit ? 'Save Changes' : 'Create Unit'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------------- Sold / Available toggle modal ---------------- //
const StatusModal = ({ open, onClose, unit, api, onSaved }) => {
  const targetSold = unit?.status !== 'sold';
  const [clientName, setClientName] = useState('');
  const [lockinDate, setLockinDate] = useState('');
  const [leaseDate, setLeaseDate] = useState('');
  const [clientNote, setClientNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setClientName(unit?.client_name || '');
      setLockinDate(unit?.lockin_expiry_date ? unit.lockin_expiry_date.slice(0, 10) : '');
      setLeaseDate(unit?.lease_expiry_date ? unit.lease_expiry_date.slice(0, 10) : '');
      setClientNote(unit?.client_note || '');
    }
  }, [open, unit]);

  const save = async () => {
    setSaving(true);
    try {
      const body = targetSold
        ? { status: 'sold', client_name: clientName.trim(), lockin_expiry_date: lockinDate, lease_expiry_date: leaseDate || null, client_note: clientNote.trim() || null }
        : { status: 'available' };
      await api.patch(`/inventory/units/${unit.id}/status`, body);
      toast.success(targetSold ? 'Marked as Sold' : 'Marked as Available');
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  if (!unit) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="inventory-status-modal">
        <DialogHeader>
          <DialogTitle>{targetSold ? `Mark "${unit.name}" as Sold` : `Mark "${unit.name}" as Available`}</DialogTitle>
        </DialogHeader>
        {targetSold ? (
          <div className="space-y-3 py-2">
            <div>
              <Label>Client / Company name *</Label>
              <Input value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="Acme Pvt Ltd" data-testid="client-name-input" />
            </div>
            <div>
              <Label>Lock-in expiry date *</Label>
              <Input type="date" value={lockinDate} onChange={(e) => setLockinDate(e.target.value)} data-testid="lockin-date-input" />
            </div>
            <div>
              <Label>Lease expiry date</Label>
              <Input type="date" value={leaseDate} onChange={(e) => setLeaseDate(e.target.value)} data-testid="lease-date-input" />
            </div>
            <div>
              <Label>Note (optional)</Label>
              <Textarea rows={2} value={clientNote} onChange={(e) => setClientNote(e.target.value)} placeholder="e.g. renewal likely, confirmed move-out" />
            </div>
          </div>
        ) : (
          <div className="py-2 text-sm text-muted-foreground">
            This will clear the current client details and mark the unit Available. Change is logged in the unit&apos;s history.
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving || (targetSold && (!clientName.trim() || !lockinDate))} data-testid="status-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------------- Renew modal ---------------- //
const RenewModal = ({ open, onClose, unit, api, onSaved }) => {
  const [lockinDate, setLockinDate] = useState('');
  const [leaseDate, setLeaseDate] = useState('');
  const [clientNote, setClientNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setLockinDate(unit?.lockin_expiry_date ? unit.lockin_expiry_date.slice(0, 10) : '');
      setLeaseDate(unit?.lease_expiry_date ? unit.lease_expiry_date.slice(0, 10) : '');
      setClientNote(unit?.client_note || '');
    }
  }, [open, unit]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/inventory/units/${unit.id}/renew`, {
        lockin_expiry_date: lockinDate || null,
        lease_expiry_date: leaseDate || null,
        client_note: clientNote.trim() || null,
      });
      toast.success('Renewed');
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Renewal failed');
    } finally {
      setSaving(false);
    }
  };

  if (!unit) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="inventory-renew-modal">
        <DialogHeader>
          <DialogTitle>Renew &quot;{unit.name}&quot;</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label>Client / Company name</Label>
            <Input value={unit.client_name || ''} readOnly disabled data-testid="renew-client-name-input" />
          </div>
          <div>
            <Label>New lock-in expiry date</Label>
            <Input type="date" value={lockinDate} onChange={(e) => setLockinDate(e.target.value)} data-testid="renew-lockin-date-input" />
          </div>
          <div>
            <Label>New lease expiry date</Label>
            <Input type="date" value={leaseDate} onChange={(e) => setLeaseDate(e.target.value)} data-testid="renew-lease-date-input" />
          </div>
          <div>
            <Label>Note (optional)</Label>
            <Textarea rows={2} value={clientNote} onChange={(e) => setClientNote(e.target.value)} placeholder="e.g. renewal likely, confirmed move-out" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving || (!lockinDate && !leaseDate)} data-testid="renew-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------------- History modal ---------------- //
const HistoryModal = ({ open, onClose, unit }) => (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="inventory-history-modal">
      <DialogHeader>
        <DialogTitle>History — {unit?.name}</DialogTitle>
      </DialogHeader>
      <div className="space-y-2 py-2">
        {(unit?.history || []).slice().reverse().map((h, i) => (
          <div key={i} className="p-3 rounded-md border border-border bg-card/40 text-xs">
            <div className="flex items-center justify-between mb-1">
              <span className="font-semibold uppercase text-[10px] tracking-wider text-primary">{h.action.replace('_', ' ')}</span>
              <span className="text-muted-foreground">{h.changed_at ? format(new Date(h.changed_at), 'PPp') : ''}</span>
            </div>
            <p className="text-muted-foreground">By <span className="text-foreground">{h.changed_by_name}</span></p>
            {h.from_status && h.to_status && <p>Status: <b>{h.from_status}</b> → <b>{h.to_status}</b></p>}
            {h.client_name && <p>Client: <b>{h.client_name}</b></p>}
            {h.lockin_expiry_date && <p>Lock-in: <b>{h.lockin_expiry_date}</b></p>}
            {h.lease_expiry_date && <p>Lease: <b>{h.lease_expiry_date}</b></p>}
            {h.note && <p className="text-muted-foreground italic">{h.note}</p>}
          </div>
        ))}
        {(!unit?.history || unit.history.length === 0) && (
          <p className="text-sm text-muted-foreground py-6 text-center">No history yet.</p>
        )}
      </div>
    </DialogContent>
  </Dialog>
);

// ---------------- Notes modal ---------------- //
// Units created before the notes log existed only have a plain `notes` string.
const notesLog = (unit) => {
  if (unit?.notes_log?.length) return unit.notes_log;
  return unit?.notes ? [{ text: unit.notes, by_name: unit.created_by_name, at: unit.updated_at || unit.created_at }] : [];
};

const NotesModal = ({ open, onClose, unit }) => (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="inventory-notes-modal">
      <DialogHeader>
        <DialogTitle>Notes — {unit?.name}</DialogTitle>
      </DialogHeader>
      <div className="space-y-2 py-2">
        {notesLog(unit).slice().reverse().map((n, i) => (
          <div key={i} className="p-3 rounded-md border border-border bg-card/40 text-xs">
            <div className="flex items-center justify-between mb-1">
              <span className="text-muted-foreground">By <span className="text-foreground">{n.by_name || 'Unknown'}</span></span>
              <span className="text-muted-foreground">{n.at ? format(new Date(n.at), 'PPp') : ''}</span>
            </div>
            {n.text ? <p className="whitespace-pre-wrap break-words">{n.text}</p> : <p className="italic text-muted-foreground">Note cleared</p>}
          </div>
        ))}
        {notesLog(unit).length === 0 && (
          <p className="text-sm text-muted-foreground py-6 text-center">No notes yet.</p>
        )}
      </div>
    </DialogContent>
  </Dialog>
);

// ---------------- Unit card ---------------- //
const UnitCard = ({ unit, canEdit, onEdit, onToggle, onArchive, onHistory, onNotes, onRenew }) => {
  const isSold = unit.status === 'sold';
  return (
    <Card className="p-4 space-y-3 hover:shadow-md transition-shadow" data-testid={`unit-card-${unit.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-heading font-semibold truncate">{unit.name}</p>
            <Badge variant="outline" className="text-[10px]">{unit.category}</Badge>
            {unit.floor && <Badge variant="secondary" className="text-[10px]">{unit.floor}</Badge>}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {unit.total_seats} seats · {(unit.components || []).map(componentLabel).join(' + ') || 'no components'}
          </p>
        </div>
        {isSold ? (
          <Popover>
            <PopoverTrigger asChild>
              <button
                className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-500/10 text-red-600 border border-red-500/30 hover:bg-red-500/20"
                data-testid={`unit-sold-badge-${unit.id}`}
              >
                Sold
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-72 text-xs space-y-1" data-testid={`unit-sold-popover-${unit.id}`}>
              <p><span className="text-muted-foreground">Client: </span><b>{unit.client_name || '—'}</b></p>
              <p><span className="text-muted-foreground">Lock-in expires: </span><b>{unit.lockin_expiry_date || '—'}</b></p>
              <p><span className="text-muted-foreground">Lease expires: </span><b>{unit.lease_expiry_date || '—'}</b></p>
              {unit.client_note && <p className="text-muted-foreground italic">&ldquo;{unit.client_note}&rdquo;</p>}
            </PopoverContent>
          </Popover>
        ) : (
          <span className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-emerald-500/10 text-emerald-600 border border-emerald-500/30" data-testid={`unit-available-badge-${unit.id}`}>
            Available
          </span>
        )}
      </div>
      <div className="flex items-center gap-1 pt-1">
        <Button size="sm" variant="ghost" onClick={() => onHistory(unit)} className="text-xs" title="View history">
          <History className="w-3 h-3 mr-1" /> History
        </Button>
        <Button size="sm" variant="ghost" onClick={() => onNotes(unit)} className="text-xs" title="View notes" data-testid={`unit-notes-btn-${unit.id}`}>
          <StickyNote className="w-3 h-3 mr-1" /> Notes{unit.notes ? ' •' : ''}
        </Button>
        {canEdit && (
          <>
            {isSold && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onRenew(unit)}
                className="text-xs ml-auto text-blue-600 border-blue-500/40 hover:bg-blue-500/10 hover:text-blue-700"
                data-testid={`unit-renew-${unit.id}`}
              >
                Renew
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => onToggle(unit)} className={`text-xs ${isSold ? '' : 'ml-auto'}`} data-testid={`unit-toggle-${unit.id}`}>
              Mark {isSold ? 'Available' : 'Sold'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => onEdit(unit)} className="text-xs" title="Edit">
              <Edit3 className="w-3 h-3" />
            </Button>
            <Button size="sm" variant="ghost" onClick={() => onArchive(unit)} className="text-xs text-red-500 hover:text-red-600" title="Archive">
              <Trash2 className="w-3 h-3" />
            </Button>
          </>
        )}
      </div>
    </Card>
  );
};

// ---------------- Manage Inventory Managers modal (admin only) ---------------- //
const MAX_IM_PER_VENUE = 10; // keep in sync with backend/routes/inventory.py::_MAX_IM_PER_VENUE

const ManageIMsModal = ({ open, onClose, venueId, api, onSaved }) => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState('');

  const load = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    try {
      // Fetch all CM + Sales users
      const res = await api.get('/users');
      const all = Array.isArray(res.data) ? res.data : (res.data.users || []);
      const eligible = all.filter(u => u.role === 'center_manager' || u.role === 'sales');
      setCandidates(eligible);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [api, venueId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const flaggedForVenue = candidates.filter(u => (u.inventory_manager_venues || []).includes(venueId));
  const capReached = flaggedForVenue.length >= MAX_IM_PER_VENUE;
  const centerManagers = candidates.filter(u => u.role === 'center_manager');
  const perVenue = venueId !== ALL_CENTRES;

  const toggleAllCentres = async (target) => {
    const enable = !target.inventory_all_centres_access;
    setSaving(`all-${target.id}`);
    try {
      await api.post(`/inventory/managers/${target.id}/all-centres`, { enabled: enable });
      toast.success(enable ? `${target.name} can now manage inventory of all centres` : `Removed all-centre access for ${target.name}`);
      await load();
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally {
      setSaving('');
    }
  };

  const toggle = async (user, currentlyFlagged) => {
    setSaving(user.id);
    try {
      await api.post(`/inventory/managers/${user.id}/flag`, { venue_id: venueId, flagged: !currentlyFlagged });
      toast.success(currentlyFlagged ? `Unflagged ${user.name}` : `Flagged ${user.name}`);
      await load();
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally {
      setSaving('');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="manage-ims-modal">
        <DialogHeader>
          <DialogTitle>Manage Inventory Managers</DialogTitle>
          {perVenue && (
            <p className="text-xs text-muted-foreground">
              Flag up to <b>{MAX_IM_PER_VENUE}</b> Center Managers or Sales team members per venue. Flagged users can create/edit units and toggle Sold/Available status.
            </p>
          )}
        </DialogHeader>
        {loading ? (
          <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : (
          <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2" data-testid="all-centres-access-section">
              <p className="text-sm font-semibold">All Centre Inventory Access</p>
              <p className="text-xs text-muted-foreground">
                Center Managers with this access can create/edit units and toggle Sold/Available status in <b>every</b> centre, not just flagged ones.
              </p>
              {centerManagers.length === 0 && <p className="text-sm text-muted-foreground italic">No Center Managers found.</p>}
              {centerManagers.map(u => (
                <div key={u.id} className="flex items-center justify-between p-2 rounded-md border border-border">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{u.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                  </div>
                  <Button
                    size="sm"
                    variant={u.inventory_all_centres_access ? 'default' : 'outline'}
                    disabled={saving === `all-${u.id}`}
                    onClick={() => toggleAllCentres(u)}
                    data-testid={`toggle-all-centres-${u.id}`}
                  >
                    {saving === `all-${u.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : (u.inventory_all_centres_access ? 'Enabled ✓' : 'Enable')}
                  </Button>
                </div>
              ))}
            </div>
            {perVenue && (
            <div className="space-y-2">
            <p className="text-sm font-semibold">Flag for this venue</p>
            <div className="flex items-center gap-2 text-xs">
              <Badge variant={capReached ? 'destructive' : 'outline'}>{flaggedForVenue.length} / {MAX_IM_PER_VENUE} flagged for this venue</Badge>
            </div>
            {candidates.length === 0 && <p className="text-sm text-muted-foreground italic">No CM or Sales users found.</p>}
            {candidates.map(u => {
              const isFlagged = (u.inventory_manager_venues || []).includes(venueId);
              const disabled = !isFlagged && capReached;
              return (
                <div key={u.id} className="flex items-center justify-between p-2 rounded-md border border-border">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{u.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{u.email} · <span className="uppercase">{u.role.replace('_', ' ')}</span></p>
                  </div>
                  <Button
                    size="sm"
                    variant={isFlagged ? 'default' : 'outline'}
                    disabled={disabled || saving === u.id}
                    onClick={() => toggle(u, isFlagged)}
                    data-testid={`toggle-im-${u.id}`}
                  >
                    {saving === u.id ? <Loader2 className="w-3 h-3 animate-spin" /> : (isFlagged ? 'Flagged ✓' : 'Flag')}
                  </Button>
                </div>
              );
            })}
            </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------------- Main tab ---------------- //
export const InventoryTab = ({ api, user, venues: allVenues = [], initialSubTab, onInitialSubTabUsed, showAlerts = true }) => {
  const [perms, setPerms] = useState(null);
  // Center Managers only get the centres they're assigned to (visible_venue_ids is
  // null for roles/CMs with all-centre access).
  const venues = useMemo(
    () => (perms?.visible_venue_ids ? allVenues.filter(v => perms.visible_venue_ids.includes(v.id)) : allVenues),
    [allVenues, perms]
  );
  const [selectedVenue, setSelectedVenue] = useState('');
  const [units, setUnits] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formUnit, setFormUnit] = useState(undefined); // undefined = closed, null = create-new, object = edit
  const [statusUnit, setStatusUnit] = useState(null);
  const [renewUnit, setRenewUnit] = useState(null);
  const [historyUnit, setHistoryUnit] = useState(null);
  const [notesUnit, setNotesUnit] = useState(null);
  const [manageIMs, setManageIMs] = useState(false);
  const [subTab, setSubTab] = useState(initialSubTab || 'units'); // 'units' | 'available_from'

  useEffect(() => { if (initialSubTab) onInitialSubTabUsed?.(); }, [initialSubTab, onInitialSubTabUsed]);

  useEffect(() => {
    api.get('/inventory/me/permissions').then(r => setPerms(r.data)).catch(() => {});
  }, [api]);

  // Auto-pick a sensible default venue.
  // Sales users default to the cross-centre view (per Phase 3 spec).
  // Everyone else defaults to their own centre if any, otherwise the first venue.
  useEffect(() => {
    if (!selectedVenue && venues.length && perms) {
      if (perms.role === 'sales') {
        setSelectedVenue(ALL_CENTRES);
      } else if (user?.center_id && venues.find(v => v.id === user.center_id)) {
        setSelectedVenue(user.center_id);
      } else {
        setSelectedVenue(venues[0].id);
      }
    }
  }, [venues, user, selectedVenue, perms]);

  const load = useCallback(async () => {
    if (!selectedVenue) return;
    setLoading(true);
    try {
      const isAll = selectedVenue === ALL_CENTRES;
      const [unitsRes, summaryRes] = await Promise.all([
        api.get('/inventory/units', { params: isAll ? {} : { venue_id: selectedVenue } }),
        api.get('/inventory/summary', { params: isAll ? {} : { venue_id: selectedVenue } }),
      ]);
      setUnits(unitsRes.data.units || []);
      setSummary(summaryRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load inventory');
    } finally {
      setLoading(false);
    }
  }, [api, selectedVenue]);

  useEffect(() => { load(); }, [load]);

  const canEditSelected = useMemo(() => {
    if (!perms) return false;
    if (selectedVenue === ALL_CENTRES) return false; // cross-centre view is read-only
    if (perms.read_only) return false;
    if (perms.is_admin) return true;
    if (perms.role === 'center_manager' && perms.inventory_all_centres_access) return true;
    return (perms.inventory_manager_venues || []).includes(selectedVenue);
  }, [perms, selectedVenue]);

  const isCrossCentre = selectedVenue === ALL_CENTRES;
  const floorOrder = useMemo(
    () => (isCrossCentre ? [] : venues.find(v => v.id === selectedVenue)?.floors || []),
    [venues, selectedVenue, isCrossCentre]
  );
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [floorFilter, setFloorFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [view, setViewState] = useState(() => {
    try { const v = localStorage.getItem('inventory_view'); return v === 'matrix' ? 'matrix' : 'cards'; } catch { return 'cards'; }
  });
  const setView = (v) => { setViewState(v); try { localStorage.setItem('inventory_view', v); } catch { /* ignore */ } };
  // Filters are per-venue; reset when switching centre.
  useEffect(() => { setQuery(''); setStatusFilter('all'); setFloorFilter('all'); setCategoryFilter('all'); }, [selectedVenue]);

  const floorFilterOptions = useMemo(() => {
    const extra = [...new Set(units.map(u => u.floor).filter(Boolean))].filter(f => !floorOrder.includes(f)).sort();
    return [...floorOrder, ...extra];
  }, [units, floorOrder]);
  const filtersActive = !!query.trim() || statusFilter !== 'all' || floorFilter !== 'all' || categoryFilter !== 'all';
  const visibleUnits = useMemo(() => {
    const q = query.trim().toLowerCase();
    return units.filter(u => {
      if (statusFilter !== 'all' && u.status !== statusFilter) return false;
      if (categoryFilter !== 'all' && u.category !== categoryFilter) return false;
      if (floorFilter === '__none__' ? !!u.floor : (floorFilter !== 'all' && u.floor !== floorFilter)) return false;
      if (!q) return true;
      const hay = [u.name, u.floor, u.client_name, u.notes, ...(u.components || []).map(componentLabel)].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [units, query, statusFilter, floorFilter, categoryFilter]);
  const moAll = units.filter(u => u.category === 'MO');
  const cwAll = units.filter(u => u.category === 'CW');
  const moUnits = visibleUnits.filter(u => u.category === 'MO');
  const cwUnits = visibleUnits.filter(u => u.category === 'CW');

  const [floorMaps, setFloorMaps] = useState({});
  useEffect(() => {
    if (isCrossCentre || !selectedVenue) { setFloorMaps({}); return; }
    api.get('/inventory/floor-maps', { params: { venue_id: selectedVenue } })
      .then(r => setFloorMaps(r.data.floor_maps || {}))
      .catch(() => setFloorMaps({}));
  }, [api, selectedVenue, isCrossCentre]);

  const uploadFloorMap = async (floor, file) => {
    const form = new FormData();
    form.append('venue_id', selectedVenue);
    form.append('floor', floor);
    form.append('file', file);
    try {
      const res = await api.post('/inventory/floor-maps', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setFloorMaps(prev => ({ ...prev, [floor]: res.data.floor_map }));
      toast.success(`Floor map saved for ${floor}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Floor map upload failed');
    }
  };

  const deleteFloorMap = async (floor) => {
    if (!window.confirm(`Remove the floor map for ${floor}?`)) return;
    try {
      await api.delete('/inventory/floor-maps', { params: { venue_id: selectedVenue, floor } });
      setFloorMaps(prev => { const n = { ...prev }; delete n[floor]; return n; });
      toast.success('Floor map removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove floor map');
    }
  };

  const archive = async (unit) => {
    if (!window.confirm(`Archive "${unit.name}"? History is preserved but the unit will no longer appear in the list.`)) return;
    try {
      await api.delete(`/inventory/units/${unit.id}`);
      toast.success('Unit archived');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Archive failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="inventory-tab">
      {/* Expiry alerts banner (only shows if there are units in 30/15/7d buckets) */}
      {showAlerts && <AlertsBanner api={api} isAdmin={!!perms?.is_admin} onViewAll={() => setSubTab('available_from')} />}

      {/* Venue selector + create button */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Building2 className="w-4 h-4 text-muted-foreground shrink-0" />
          <Select value={selectedVenue} onValueChange={setSelectedVenue}>
            <SelectTrigger className="w-full sm:w-64" data-testid="inventory-venue-select"><SelectValue placeholder="Select venue" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_CENTRES}>
                <span className="inline-flex items-center gap-1.5"><Globe className="w-3.5 h-3.5" /> All Centres (cross-centre view)</span>
              </SelectItem>
              {venues.map(v => (
                <SelectItem key={v.id} value={v.id}>{v.name} · {v.city}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="sm:ml-auto flex flex-wrap items-center gap-2">
          {isCrossCentre && (
            <Badge variant="outline" className="text-xs whitespace-normal">Cross-centre view — read-only, use filters below</Badge>
          )}
          {!isCrossCentre && perms && !canEditSelected && (
            <Badge variant="outline" className="text-xs whitespace-normal">
              {perms.read_only ? 'Read-only access' : 'Read-only — not flagged as Inventory Manager for this venue'}
            </Badge>
          )}
          {perms?.is_admin && (
            <Button variant="outline" size="sm" onClick={() => setManageIMs(true)} data-testid="manage-ims-btn">
              <ShieldCheck className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Manage IMs</span>
            </Button>
          )}
          {canEditSelected && (
            <Button size="sm" onClick={() => setFormUnit(null)} data-testid="create-unit-btn">
              <Plus className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Add Unit</span>
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const url = new URL(`${api.defaults.baseURL}/inventory/export.csv`);
              if (!isCrossCentre) url.searchParams.set('venue_id', selectedVenue);
              const token = localStorage.getItem('token');
              // Fetch as blob and trigger download so the Authorization header is preserved.
              fetch(url.toString(), { headers: { Authorization: `Bearer ${token}` } })
                .then(r => r.blob())
                .then(blob => {
                  const link = document.createElement('a');
                  link.href = URL.createObjectURL(blob);
                  link.download = `inventory_${isCrossCentre ? 'all' : selectedVenue.slice(0, 8)}_${new Date().toISOString().slice(0, 10)}.csv`;
                  link.click();
                  URL.revokeObjectURL(link.href);
                })
                .catch(() => toast.error('Export failed'));
            }}
            data-testid="export-csv-btn"
          >
            <Download className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Export CSV</span>
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="inventory-summary">
          <SummaryCard  icon={Boxes} label="Total seats" value={summary.total.total_seats} sub={`${summary.total.total_units} units`} />
          <SummaryCard unit="seats" icon={DoorOpen} label="Available" value={summary.total.available_seats} sub={`${summary.total.available_units} units`} tone="emerald" />
          <SummaryCard unit="seats" icon={Users} label="Sold" value={summary.total.sold_seats} sub={`${summary.total.sold_units} units`} tone="red" />
          <SummaryCard unit="seats" icon={Building2} label="Available · MO / CW" value={`${summary.buckets.MO.available_seats} / ${summary.buckets.CW.available_seats}`} sub={`${summary.buckets.MO.available_units} / ${summary.buckets.CW.available_units} units available`} tone="emerald" />
        </div>
      )}

      {/* Sub-tabs: Units / Available From / History */}
      <div className="border-b border-border flex items-center gap-1 overflow-x-auto clay-scroll-x" data-testid="inventory-subtabs">
        <button
          onClick={() => setSubTab('units')}
          className={`shrink-0 whitespace-nowrap px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            subTab === 'units' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          data-testid="subtab-units"
        >
          <Boxes className="w-3.5 h-3.5 inline mr-1.5" /> Units
        </button>
        <button
          onClick={() => setSubTab('available_from')}
          className={`shrink-0 whitespace-nowrap px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            subTab === 'available_from' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          data-testid="subtab-available-from"
        >
          <CalendarClock className="w-3.5 h-3.5 inline mr-1.5" /> Lock-in Expiries
        </button>
        <button
          onClick={() => setSubTab('history')}
          className={`shrink-0 whitespace-nowrap px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            subTab === 'history' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          data-testid="subtab-history"
        >
          <History className="w-3.5 h-3.5 inline mr-1.5" /> History
        </button>
      </div>

      {/* Loading */}
      {loading && <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>}

      {/* Unit sections */}
      {!loading && subTab === 'units' && !isCrossCentre && (
        <>
          {/* Search / filter / view toolbar */}
          <div className="flex flex-wrap items-center gap-2" data-testid="inventory-filters">
            <div className="relative w-full sm:w-64">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
              <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, client, floor…" className="pl-8 h-9" data-testid="inventory-search" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-32 h-9" data-testid="filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All status</SelectItem>
                <SelectItem value="available">Available</SelectItem>
                <SelectItem value="sold">Sold</SelectItem>
              </SelectContent>
            </Select>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-36 h-9" data-testid="filter-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">MO + Coworking</SelectItem>
                <SelectItem value="MO">Managed Offices</SelectItem>
                <SelectItem value="CW">Coworking</SelectItem>
              </SelectContent>
            </Select>
            <Select value={floorFilter} onValueChange={setFloorFilter}>
              <SelectTrigger className="w-36 h-9" data-testid="filter-floor"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All floors</SelectItem>
                {floorFilterOptions.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                <SelectItem value="__none__">No floor</SelectItem>
              </SelectContent>
            </Select>
            {filtersActive && (
              <Button variant="ghost" size="sm" className="h-9" onClick={() => { setQuery(''); setStatusFilter('all'); setFloorFilter('all'); setCategoryFilter('all'); }}>
                <X className="w-3.5 h-3.5 mr-1" /> Clear
              </Button>
            )}
            <div className="ml-auto inline-flex rounded-md border border-border overflow-hidden" data-testid="view-toggle">
              <button type="button" onClick={() => setView('cards')} title="Card view"
                className={`px-2.5 py-1.5 ${view === 'cards' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}><LayoutGrid className="w-4 h-4" /></button>
              <button type="button" onClick={() => setView('matrix')} title="Status matrix"
                className={`px-2.5 py-1.5 ${view === 'matrix' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}><Table2 className="w-4 h-4" /></button>
            </div>
          </div>
          <UnitSection title="Managed Offices" subtitle="≥ 20 seats" units={moUnits} totalCount={moAll.length} filtered={filtersActive} view={view} floorOrder={floorOrder} canEdit={canEditSelected}
            floorMaps={floorMaps} apiBase={api.defaults.baseURL} onUploadFloorMap={uploadFloorMap} onDeleteFloorMap={deleteFloorMap}
            onEdit={setFormUnit} onToggle={setStatusUnit} onArchive={archive} onHistory={setHistoryUnit} onNotes={setNotesUnit} onRenew={setRenewUnit} />
          <UnitSection title="Coworking" subtitle="< 20 seats" units={cwUnits} totalCount={cwAll.length} filtered={filtersActive} view={view} floorOrder={floorOrder} canEdit={canEditSelected}
            floorMaps={floorMaps} apiBase={api.defaults.baseURL} onUploadFloorMap={uploadFloorMap} onDeleteFloorMap={deleteFloorMap}
            onEdit={setFormUnit} onToggle={setStatusUnit} onArchive={archive} onHistory={setHistoryUnit} onNotes={setNotesUnit} onRenew={setRenewUnit} />
        </>
      )}

      {!loading && subTab === 'units' && isCrossCentre && (
        <CrossCentreView units={units} venues={venues} />
      )}

      {!loading && subTab === 'available_from' && (
        <AvailableFromView units={units} venues={venues} showVenue={isCrossCentre} />
      )}

      {!loading && subTab === 'history' && (
        <HistoryTimelineView api={api} venueId={isCrossCentre ? null : selectedVenue} />
      )}

      <UnitFormModal open={formUnit !== undefined} onClose={() => setFormUnit(undefined)} unit={formUnit || null}
        venueId={selectedVenue} venues={venues} api={api} onSaved={load} />
      <StatusModal open={!!statusUnit} onClose={() => setStatusUnit(null)} unit={statusUnit} api={api} onSaved={load} />
      <RenewModal open={!!renewUnit} onClose={() => setRenewUnit(null)} unit={renewUnit} api={api} onSaved={load} />
      <HistoryModal open={!!historyUnit} onClose={() => setHistoryUnit(null)} unit={historyUnit} />
      <NotesModal open={!!notesUnit} onClose={() => setNotesUnit(null)} unit={notesUnit} />
      <ManageIMsModal open={manageIMs} onClose={() => setManageIMs(false)} venueId={selectedVenue} api={api}
        onSaved={() => api.get('/inventory/me/permissions').then(r => setPerms(r.data)).catch(() => {})} />
    </div>
  );
};

const SummaryCard = ({ icon: Icon, label, value, sub, tone, unit }) => {
  const toneClasses = tone === 'emerald' ? 'text-emerald-600' : tone === 'red' ? 'text-red-600' : 'text-primary';
  return (
    <Card className="p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${toneClasses}`} />
        <span className="text-xs text-muted-foreground uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-2xl font-heading font-bold">{value}{unit && <span className="ml-1.5 text-sm font-normal text-muted-foreground">{unit}</span>}</p>
      <p className="text-xs text-muted-foreground mt-1">{sub}</p>
    </Card>
  );
};

// Groups units by floor, ordered per the venue's configured `floors` list.
// Floors present on units but missing from that config (legacy data) sort
// alphabetically after configured floors; units with no floor go last.
const groupUnitsByFloor = (units, floorOrder) => {
  const UNASSIGNED = '__unassigned__';
  const map = new Map();
  for (const u of units) {
    const key = u.floor || UNASSIGNED;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(u);
  }
  const orderedKeys = floorOrder.filter(f => map.has(f));
  const extra = [...map.keys()].filter(k => k !== UNASSIGNED && !floorOrder.includes(k)).sort();
  orderedKeys.push(...extra);
  if (map.has(UNASSIGNED)) orderedKeys.push(UNASSIGNED);
  // Available units first, sold after (Array.sort is stable, so original order is kept within each).
  const soldRank = (u) => (u.status === 'sold' ? 1 : 0);
  return orderedKeys.map(key => ({
    floor: key === UNASSIGNED ? null : key,
    units: [...map.get(key)].sort((a, b) => soldRank(a) - soldRank(b)),
  }));
};

// Per-floor map: anyone can view; only users with inventory access (canEdit) can upload/replace/remove.
const FloorMapControl = ({ floor, map, canEdit, apiBase, onUpload, onDelete }) => {
  const inputRef = React.useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    try { await onUpload(floor, file); } finally { setBusy(false); }
  };
  // `url` is stored as /api/files/...; resolve against the backend host.
  const href = map ? `${(apiBase || '').replace(/\/api\/?$/, '')}${map.url}` : null;
  const isImage = (map?.content_type || '').startsWith('image/');
  const [open, setOpen] = useState(false);
  return (
    <div className="flex items-center gap-1.5 ml-2" data-testid={`floor-map-${floor}`}>
      {map && isImage && (
        <>
          <button type="button" onClick={() => setOpen(true)} title={`View ${floor} map`}
            className="h-10 w-14 rounded border border-border overflow-hidden bg-muted shrink-0" data-testid={`floor-map-thumb-${floor}`}>
            <img src={href} alt={`${floor} map`} className="h-full w-full object-cover" loading="lazy" />
          </button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent className="max-w-4xl">
              <DialogHeader><DialogTitle>{floor} — Floor Map</DialogTitle></DialogHeader>
              <img src={href} alt={`${floor} map`} className="w-full max-h-[75vh] object-contain" />
            </DialogContent>
          </Dialog>
        </>
      )}
      {map && !isImage && (
        <a href={href} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline inline-flex items-center gap-1">
          <MapIcon className="w-3 h-3" /> View Floor Map (PDF)
        </a>
      )}
      {canEdit && (
        <>
          <input ref={inputRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={pick} />
          <Button variant="outline" size="sm" className="h-6 px-2 text-xs" disabled={busy}
            onClick={() => inputRef.current?.click()} data-testid={`upload-floor-map-${floor}`}>
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
            {map ? 'Replace Floor Map' : 'Upload Floor Map'}
          </Button>
          {map && (
            <Button variant="ghost" size="sm" className="h-6 px-1.5 text-red-600" onClick={() => onDelete(floor)} title="Remove floor map">
              <Trash2 className="w-3 h-3" />
            </Button>
          )}
        </>
      )}
    </div>
  );
};

const PAGE_SIZE = 24;

// Dense status matrix (rows = units, columns = attributes) with colour-coded cells.
const daysUntil = (d) => {
  if (!d) return null;
  const t = new Date(d);
  return Number.isNaN(t.getTime()) ? null : Math.ceil((t - new Date()) / 86400000);
};
const expiryTone = (d, sold) => {
  const n = daysUntil(d);
  if (!sold || n === null) return '';
  if (n < 0) return 'bg-red-500/20 text-red-700';
  if (n <= 30) return 'bg-amber-500/20 text-amber-700';
  return 'bg-sky-500/10 text-sky-700';
};

const UnitMatrix = ({ units, canEdit, onEdit, onToggle, onArchive, onHistory, onNotes, onRenew }) => (
  <div className="border border-border rounded-md overflow-x-auto bg-card" data-testid="unit-matrix">
    <table className="w-full text-sm border-collapse min-w-[1000px]">
      <thead>
        <tr className="bg-muted/50 text-xs text-muted-foreground uppercase tracking-wider">
          {['Unit', 'Type', 'Seats', 'Components', 'Status', 'Client', 'Lock-in expiry', 'Lease expiry', ''].map(h => (
            <th key={h || 'actions'} className={`text-left font-medium px-3 py-2 border-b border-border whitespace-nowrap bg-muted ${h === 'Unit' ? 'sticky left-0 z-[1]' : ''} ${h === '' ? 'sticky right-0 border-l' : ''}`}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {units.map(u => {
          const sold = u.status === 'sold';
          return (
            <tr key={u.id} className="group border-b border-border last:border-b-0 hover:bg-muted/30" data-testid={`unit-matrix-row-${u.id}`}>
              <td className="px-3 py-1.5 font-medium whitespace-nowrap sticky left-0 bg-card group-hover:bg-muted z-[1]">{u.name}</td>
              <td className="px-3 py-1.5"><Badge variant="outline" className="text-[10px]">{u.category}</Badge></td>
              <td className="px-3 py-1.5 text-muted-foreground">{u.total_seats}</td>
              <td className="px-3 py-1.5 text-xs text-muted-foreground">{(u.components || []).map(componentLabel).join(' + ') || '—'}</td>
              <td className={`px-3 py-1.5 text-center text-xs font-medium border-x border-border ${sold ? 'bg-red-500/10 text-red-600' : 'bg-emerald-500/10 text-emerald-600'}`}>
                {sold ? 'Sold' : 'Available'}
              </td>
              <td className="px-3 py-1.5 text-xs max-w-[180px] truncate" title={u.client_name || ''}>{sold ? (u.client_name || '—') : ''}</td>
              <td className={`px-3 py-1.5 text-xs whitespace-nowrap ${expiryTone(u.lockin_expiry_date, sold)}`}>{sold ? (u.lockin_expiry_date || '—') : ''}</td>
              <td className={`px-3 py-1.5 text-xs whitespace-nowrap ${expiryTone(u.lease_expiry_date, sold)}`}>{sold ? (u.lease_expiry_date || '—') : ''}</td>
              <td className="px-2 py-1 whitespace-nowrap sticky right-0 bg-card group-hover:bg-muted border-l border-border">
                <div className="flex items-center justify-end gap-1">
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onHistory(u)} title="History"><History className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 relative" onClick={() => onNotes(u)} title="Notes">
                    <StickyNote className="w-3.5 h-3.5" />
                    {u.notes ? <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-primary" /> : null}
                  </Button>
                  {canEdit && (
                    <>
                      {/* fixed-width slots keep the icons aligned in every row, sold or not */}
                      <div className="w-[68px]">
                        {sold && <Button size="sm" variant="outline" className="h-7 w-full text-xs text-blue-600" onClick={() => onRenew(u)}>Renew</Button>}
                      </div>
                      <Button size="sm" variant="outline" className="h-7 w-[112px] text-xs" onClick={() => onToggle(u)}>Mark {sold ? 'Available' : 'Sold'}</Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onEdit(u)} title="Edit"><Edit3 className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => onArchive(u)} title="Archive"><Trash2 className="w-3.5 h-3.5" /></Button>
                    </>
                  )}
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const StatusCounts = ({ units }) => {
  const seats = (list) => list.reduce((n, u) => n + (Number(u.total_seats) || 0), 0);
  const soldUnits = units.filter(u => u.status === 'sold');
  const availUnits = units.filter(u => u.status !== 'sold');
  return (
    <span className="text-xs inline-flex flex-wrap items-center gap-x-1.5" data-testid="status-counts">
      <span className="text-muted-foreground">{seats(units)} seats</span>
      <span className="text-muted-foreground">·</span>
      <span className="text-emerald-600">{availUnits.length} available ({seats(availUnits)} seats)</span>
      <span className="text-muted-foreground">·</span>
      <span className="text-red-600">{soldUnits.length} sold ({seats(soldUnits)} seats)</span>
    </span>
  );
};

// One floor's units, with its own "show more" paging so a floor with hundreds of units stays light.
const FloorGroup = ({ g, showHeader, collapsed, onToggle, view, floorOrder, floorMaps, apiBase, onUploadFloorMap, onDeleteFloorMap, actions }) => {
  const [limit, setLimit] = useState(PAGE_SIZE);
  const open = !collapsed;
  const shown = g.units.slice(0, limit);
  const rest = g.units.length - shown.length;
  return (
    <div className="space-y-2" data-testid={`floor-group-${g.floor || 'unassigned'}`}>
      {showHeader && (
        <div className="flex flex-wrap items-center gap-2 pl-1">
          <button type="button" onClick={onToggle} className="flex items-center gap-2 hover:opacity-80" title={open ? 'Collapse' : 'Expand'}>
            {open ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
            <Building2 className="w-5 h-5 text-muted-foreground" />
            <h4 className="text-lg font-semibold text-foreground">{g.floor || 'Unassigned floor'}</h4>
          </button>
          <span className="text-xs text-muted-foreground">· {g.units.length} unit{g.units.length !== 1 ? 's' : ''}</span>
          <StatusCounts units={g.units} />
          {g.floor && onUploadFloorMap && floorOrder.includes(g.floor) && (
            <FloorMapControl floor={g.floor} map={floorMaps[g.floor]} canEdit={actions.canEdit} apiBase={apiBase}
              onUpload={onUploadFloorMap} onDelete={onDeleteFloorMap} />
          )}
        </div>
      )}
      {open && (
        <>
          {view === 'matrix' ? (
            <UnitMatrix units={shown} {...actions} />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {shown.map(u => <UnitCard key={u.id} unit={u} {...actions} />)}
            </div>
          )}
          {rest > 0 && (
            <div className="flex justify-center gap-2">
              <Button size="sm" variant="outline" onClick={() => setLimit(l => l + PAGE_SIZE)}>Show {Math.min(rest, PAGE_SIZE)} more ({rest} remaining)</Button>
              <Button size="sm" variant="ghost" onClick={() => setLimit(g.units.length)}>Show all</Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const UnitSection = ({ title, subtitle, units, totalCount, filtered, view = 'cards', floorOrder = [], floorMaps = {}, apiBase, onUploadFloorMap, onDeleteFloorMap, ...actions }) => {
  const groups = useMemo(() => groupUnitsByFloor(units, floorOrder), [units, floorOrder]);
  const [sectionCollapsed, setSectionCollapsed] = useState(false);
  const [collapsedFloors, setCollapsedFloors] = useState({});
  const keyOf = (g) => g.floor || '__u';
  const allCollapsed = groups.length > 0 && groups.every(g => collapsedFloors[keyOf(g)]);
  const setAll = (val) => setCollapsedFloors(Object.fromEntries(groups.map(g => [keyOf(g), val])));
  const showHeader = floorOrder.length > 0 || groups.length > 1;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => setSectionCollapsed(c => !c)} className="flex items-center gap-1.5 hover:opacity-80" data-testid={`section-toggle-${title}`}>
          {sectionCollapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          <h3 className="font-heading font-semibold text-lg">{title}</h3>
        </button>
        <span className="text-xs text-muted-foreground">
          {subtitle} · {filtered ? `${units.length} of ${totalCount}` : units.length} unit{units.length !== 1 ? 's' : ''}
        </span>
        <StatusCounts units={units} />
        {!sectionCollapsed && showHeader && groups.length > 1 && (
          <Button size="sm" variant="ghost" className="h-6 text-xs ml-auto" onClick={() => setAll(!allCollapsed)}>
            {allCollapsed ? 'Expand all floors' : 'Collapse all floors'}
          </Button>
        )}
      </div>
      {!sectionCollapsed && (units.length === 0 ? (
        <p className="text-sm text-muted-foreground italic py-4 border border-dashed border-border rounded-md text-center">
          {filtered ? 'No units match your filters.' : 'No units in this bucket yet.'}
        </p>
      ) : (
        <div className="space-y-4">
          {groups.map(g => (
            <FloorGroup key={g.floor || 'unassigned'} g={g} showHeader={showHeader} view={view}
              collapsed={!!collapsedFloors[keyOf(g)]} onToggle={() => setCollapsedFloors(p => ({ ...p, [keyOf(g)]: !p[keyOf(g)] }))}
              floorOrder={floorOrder} floorMaps={floorMaps} apiBase={apiBase}
              onUploadFloorMap={onUploadFloorMap} onDeleteFloorMap={onDeleteFloorMap} actions={actions} />
          ))}
        </div>
      ))}
    </div>
  );
};
